import equal from "fast-deep-equal"
import type {
  Bindings,
  PatchPublicationDto,
  Publication,
} from "../interface/publications"
import type {
  PatchPlacementDto,
  Placement,
  Schedule,
} from "../interface/schedule"
import { isBinding, toBindingWrite } from "./bindings"

export type PublicationEditable = Pick<
  Publication,
  "name" | "templateVersionId" | "bindings"
>
export type PlacementEditable = Pick<
  Placement,
  "channelId" | "schedule" | "enabled" | "pin" | "deletePrevious"
>

function assertRevision(revision: number) {
  if (!Number.isSafeInteger(revision) || revision < 1)
    throw new TypeError("Invalid revision")
}

function assertString(value: unknown): asserts value is string {
  if (typeof value !== "string") throw new TypeError("Expected a string")
}

/** Only explicit editable values. Undefined means omitted, never a deletion/default.
 * A null result must become SKIP_FORM_SUBMIT at the RtkHookForm boundary. */
export function buildPublicationPatch(
  baseline: PublicationEditable & { revision: number },
  current: Partial<Publication>,
): PatchPublicationDto | null {
  const changes: Partial<PublicationEditable> & { bindings?: Bindings } = {}
  for (const field of ["name", "templateVersionId"] as const) {
    if (
      current[field] !== undefined &&
      !equal(baseline[field], current[field])
    ) {
      assertString(current[field])
      changes[field] = current[field]
    }
  }
  if (current.bindings !== undefined) {
    if (
      !current.bindings ||
      typeof current.bindings !== "object" ||
      Array.isArray(current.bindings)
    )
      throw new TypeError("Invalid bindings")
    const bindings: Bindings = {}
    for (const [id, value] of Object.entries(current.bindings)) {
      if (equal(baseline.bindings[id], value)) continue
      const replacement = toBindingWrite(value)
      const before = baseline.bindings[id]
      if (isBinding(before) && equal(toBindingWrite(before), replacement))
        continue
      Object.defineProperty(bindings, id, {
        value: replacement,
        enumerable: true,
      })
    }
    if (Object.keys(bindings).length) changes.bindings = bindings
  }
  if (!Object.keys(changes).length) return null
  assertRevision(baseline.revision)
  // At least one whitelisted property is present, as checked above.
  return { revision: baseline.revision, ...changes } as PatchPublicationDto
}

/** No Date conversion here. Date-input/timezone handling belongs to the schedule UI. */
function scheduleValue(schedule: Schedule): Schedule {
  if (!schedule || !Array.isArray(schedule.schedules))
    throw new TypeError("Invalid schedule")
  const result: Schedule = {
    schedules: schedule.schedules.map((rule) => {
      if (!rule) throw new TypeError("Invalid schedule rule")
      if (rule.type === "interval") {
        if (
          !["m", "h", "d"].includes(rule.period) ||
          typeof rule.value !== "number" ||
          !Number.isFinite(rule.value)
        )
          throw new TypeError("Invalid interval rule")
        return { type: rule.type, period: rule.period, value: rule.value }
      }
      if (
        rule.type !== "schedule" ||
        ![rule.weekDays, rule.hour, rule.minute].every(
          (values) =>
            Array.isArray(values) &&
            values.every(
              (value) => typeof value === "number" && Number.isFinite(value),
            ),
        ) ||
        typeof rule.tz !== "number" ||
        !Number.isFinite(rule.tz)
      )
        throw new TypeError("Invalid schedule rule")
      return {
        type: rule.type,
        weekDays: [...rule.weekDays],
        hour: [...rule.hour],
        minute: [...rule.minute],
        tz: rule.tz,
      }
    }),
  }
  if (schedule.TZ !== undefined) {
    if (schedule.TZ === null)
      throw new TypeError("Only schedule dates may be null")
    result.TZ = schedule.TZ
  }
  for (const field of ["dateStart", "dateEnd"] as const) {
    if (schedule[field] !== undefined) {
      if (schedule[field] !== null) assertString(schedule[field])
      result[field] = schedule[field]
    }
  }
  return result
}

export function buildPlacementPatch(
  baseline: PlacementEditable & { revision: number },
  current: Partial<Placement>,
): PatchPlacementDto | null {
  const changes: Partial<PlacementEditable> = {}
  if (
    current.channelId !== undefined &&
    current.channelId !== baseline.channelId
  ) {
    assertString(current.channelId)
    changes.channelId = current.channelId
  }
  for (const field of ["enabled", "pin", "deletePrevious"] as const) {
    if (current[field] !== undefined && current[field] !== baseline[field]) {
      if (typeof current[field] !== "boolean")
        throw new TypeError("Expected a boolean")
      changes[field] = current[field]
    }
  }
  if (
    current.schedule !== undefined &&
    !equal(current.schedule, baseline.schedule)
  ) {
    const schedule = scheduleValue(current.schedule)
    if (!equal(schedule, scheduleValue(baseline.schedule)))
      changes.schedule = schedule
  }
  if (!Object.keys(changes).length) return null
  assertRevision(baseline.revision)
  return { revision: baseline.revision, ...changes } as PatchPlacementDto
}
